// /principles 페이지와 홈 요약이 쓰는 AI 네이티브 개발 원칙. 사례는 이력서와 앱 저장소에 기록된 사실만 쓴다.

export interface Principle {
  id: string; // 앵커, 예: p-01
  title: string;
  body: string[];
  practice: string[]; // 이렇게 합니다
  cases: { label: string; text: string; to?: string }[];
}

export const PRINCIPLES_INTRO = [
  "AI로 누구나 동작하는 코드를 만들 수 있게 됐습니다. 개발자도 코드 대부분을 AI와 함께 씁니다.",
  "반면 AI가 만든 결과를 검토하고 보증하는 단계는 자주 비어 있습니다. 사람이 확인 레이어가 되어야 하는데, 제대로 보지 않고 승인한 코드가 그대로 배포되는 경우를 많이 봤습니다.",
  "그래서 개발자의 역할은 무엇을 만들지, 어떤 기술로 만들지, 결과를 어떻게 보증할지 판단하는 일이라고 생각합니다.",
];

export const PRINCIPLES: Principle[] = [
  {
    id: "p-01",
    title: "AI가 만든 결과는 사람이 검토하고 보증한다",
    body: [
      "코드는 AI가 써도 장애의 책임은 사람에게 있습니다.",
      "검토가 사람의 주의력에만 기대지 않도록 통과 기준을 테스트와 자동 검증으로 정해 둡니다.",
    ],
    practice: [
      "테스트와 실제 실행으로 동작을 확인한 뒤 머지합니다.",
      "코드 컨벤션과 리뷰 체크리스트를 자동 검증으로 만듭니다.",
      "AI가 제시한 수치와 근거는 출처를 직접 확인합니다.",
    ],
    cases: [
      { label: "셀타스퀘어", text: "TDD와 AI 자동화로 프로젝트 전체 테스트 커버리지 100%를 유지하고, Claude Skill로 코드 컨벤션 자동 검증을 팀에 도입했습니다." },
    ],
  },
  {
    id: "p-02",
    title: "기술은 목적과 근거를 갖고 고른다",
    body: [
      "AI가 제안하는 설계는 문제 규모와 상관없이 그럴듯합니다. 그대로 따르면 지금 필요 없는 구성 요소가 늘어납니다.",
      "오버엔지니어링은 아닌지, 서비스 규모와 용도, 비용에 맞는지를 기준으로 고릅니다.",
    ],
    practice: [
      "새 구성 요소를 넣기 전에 기존 DB나 서버로 해결되는지 확인합니다.",
      "서비스 규모와 비용에 맞춰 Spring Boot, NestJS, FastAPI, Serverless 중에서 고릅니다.",
      "비교한 대안과 선택하지 않은 이유를 함께 기록합니다.",
    ],
    cases: [
      { label: "LimeLink", text: "세션, 작업 잠금, 중복 제거를 Redis 없이 PostgreSQL 테이블로 처리해 캐시 서버를 따로 운영하지 않았습니다.", to: "/apps/limelink" },
      { label: "띠롱", text: "Office 미리보기에 사용자당 과금되는 상용 제품 대신 MIT 라이선스 변환기를 골랐습니다.", to: "/apps/ddirong" },
    ],
  },
  {
    id: "p-03",
    title: "기술부채는 의도적으로 만들고, 해소 시점을 정해 둔다",
    body: [
      "처음부터 완벽한 구조를 만들면 출시가 늦어집니다. 지금 감당할 수 있는 부채는 의도적으로 남깁니다.",
      "대신 언제, 어떤 방법으로 해소할지 정해 둡니다.",
    ],
    practice: [
      "미룬 결정마다 다시 검토할 조건을 기록합니다.",
      "조건은 측정값으로 정합니다.",
    ],
    cases: [
      { label: "띠롱", text: "이미지 레지스트리 전환, 문서 변환 큐, 수평 확장을 미루면서 각각 다시 검토할 조건과 순서를 문서에 적었습니다.", to: "/apps/ddirong" },
      { label: "LimeNote", text: "작업 처리 서버 분리는 처리량이 병목이 될 때로 미루고, 설정만으로 분리할 수 있게 만들어 두었습니다.", to: "/apps/limenote" },
    ],
  },
  {
    id: "p-04",
    title: "지금 하지 않을 일부터 정한다",
    body: [
      "AI로 기능 하나를 만드는 비용이 낮아지면서 지금 필요 없는 기능도 쉽게 늘어납니다.",
      "사용자에게 지금 필요한 것과 손익에 영향을 주는 것을 먼저 합니다.",
    ],
    practice: [
      "출시 범위를 정할 때 뺄 기능부터 정합니다.",
      "비용이 드는 기능은 사용자나 기간을 제한해 엽니다.",
      "미룬 기능은 미룬 이유를 기록합니다.",
    ],
    cases: [
      { label: "독박스", text: "1.0에 분석과 크래시 수집 SDK를 넣지 않고, 출시 직후 지표는 스토어 지표와 이용자 인터뷰로 보기로 했습니다.", to: "/apps/dokbox" },
      { label: "띠롱", text: "AI 비용이 드는 기능은 운영자 승인 후 7일간만 열었습니다.", to: "/apps/ddirong" },
    ],
  },
  {
    id: "p-05",
    title: "데모와 프로덕션은 기준이 다르다",
    body: [
      "화면에서 동작하는 기능은 AI로 금방 만들 수 있습니다. 운영 환경에서는 결제 중복과 장애 시 데이터 보존까지 고려해야 합니다.",
      "프로덕션에 가까울수록 되돌리기 어려운 부분부터 검토합니다.",
    ],
    practice: [
      "결제, 데이터, 인증처럼 되돌리기 어려운 영역에 먼저 시간을 씁니다.",
      "운영 데이터 변경은 리허설을 거쳐 적용합니다.",
      "외부 서비스가 실패했을 때의 동작을 먼저 정합니다.",
    ],
    cases: [
      { label: "LimeNote", text: "비싼 음성 전사를 부르기 전에 크레딧을 먼저 차감하고, 크레딧은 추가만 하는 원장으로 관리했습니다.", to: "/apps/limenote" },
      { label: "LimeLink", text: "운영 DB 백업을 복원해 리허설한 뒤 결제 구조를 전환하고, 전환 전후 결제 데이터가 같은지 확인했습니다.", to: "/apps/limelink" },
    ],
  },
  {
    id: "p-06",
    title: "반복되는 AI 작업은 스킬, 훅, 커맨드로 묶는다",
    body: [
      "같은 작업을 매번 프롬프트로 지시하면 결과도 매번 달라집니다.",
      "반복 작업은 스킬, 훅, 커맨드로 만들어 체이닝하고, 누가 실행해도 같은 절차와 검증을 거치게 합니다.",
    ],
    practice: [
      "반복 작업은 스킬로 만들어 같은 절차로 실행합니다.",
      "빠뜨리면 안 되는 검증은 훅으로 강제합니다.",
      "에이전트를 병렬로 돌릴 때는 작업 범위가 겹치지 않게 나누고 결과는 한 곳에서 검증합니다.",
    ],
    cases: [
      { label: "셀타스퀘어", text: "Claude Skill로 코드 컨벤션 자동 검증, AI 기반 PR 자동 생성, 기획서 엣지케이스 검증기를 팀 워크플로에 도입했습니다." },
      { label: "독박스", text: "기능 폴더 하나는 에이전트 하나만 고치는 규칙으로 iOS와 Android 작업을 병렬로 진행했습니다. 첫 커밋부터 1.1.1 버전까지 8일이 걸렸습니다.", to: "/apps/dokbox" },
    ],
  },
];
